import {
  useEffect,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";

import {
  getBillingDates,
  createBillingDate,
  updateBillingDate,
} from "@/services/masters";

import MasterModal from "../MasterModal";

import MasterTable, { Column } from "../MasterTable";

import {
  useScreenPermission,
} from "@/hooks/useScreenPermission";

import AddTargetDayForm,
{
  AddTargetDayHandle,
  TargetDayFormData,
} from "./AddBillingDateForm";
import { Plus } from "lucide-react";

export interface TargetDay {
  id: number;
  From_Day: number;
  To_Day: number;
  Target_Day: number;
  Is_Active: boolean;
}

export default function BillingDates() {

  const {
    canCreate,
    canUpdate,
  } =
    useScreenPermission(
      "billing cycle"
    );

  const [
    list,
    setList,
  ] =
    useState<
      TargetDay[]
    >([]);

  const [
    editingItem,
    setEditingItem,
  ] =
    useState<
      TargetDay | null
    >(null);

  const [
    page,
    setPage,
  ] =
    useState(1);

  const [
    totalPages,
    setTotalPages,
  ] =
    useState(1);

  const [
    totalCount,
    setTotalCount,
  ] =
    useState(0);

  const [
    isLoading,
    setIsLoading,
  ] =
    useState(false);

  const [
    isModalOpen,
    setIsModalOpen,
  ] =
    useState(false);

  const formRef =
    useRef<AddTargetDayHandle>(
      null
    );

  useEffect(() => {
    loadData(
      1
    );
  }, []);

  const loadData =
    async (
      currentPage = 1
    ) => {
      try {
        setIsLoading(
          true
        );

        const {data} =
          await getBillingDates(
            currentPage
          );

        setList(data ?? []);

      } catch {

        toast.error(
          "Failed to load billing dates"
        );

      } finally {

        setIsLoading(
          false
        );
      }
    };

  const handleSubmit =
    async (
      values:
        TargetDayFormData
    ) => {

      try {

        setIsLoading(
          true
        );

        const payload =
          {
            ...values,
            Is_Active:
              true,
          };

        const response =
          editingItem
            ? await updateBillingDate(
                editingItem.id,
                payload
              )
            : await createBillingDate(
                payload
              );

        if (
          [
            200,
            201,
          ].includes(
            response.status
          )
        ) {
          toast.success(
            editingItem
              ? "Updated successfully"
              : "Created successfully"
          );

          setIsModalOpen(
            false
          );

          setEditingItem(
            null
          );

          formRef.current?.reset();

          loadData(
            page
          );
        }

      } finally {

        setIsLoading(
          false
        );
      }
    };

  const columns:
    Column<TargetDay>[] =
    [
      {
        key:
          "From_Day",

        label:
          "From Day",
      },

      {
        key:
          "To_Day",

        label:
          "To Day",
      },

      {
        key:
          "Target_Day",

        label:
          "Target Day",
      },
    ];

  return (
    <>
<div className="px-6 py-4 rounded-t-xl shadow-lg" style={{ background: 'linear-gradient(135deg, #2d3440 0%, #535c6b 100%)' }}>
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-2xl font-bold text-white">Billing Dates Management</h2>
                        <p className="text-white text-sm mt-1">Create, edit, and manage billing dates</p>
                    </div>
                    {canCreate && (
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-semibold py-2.5 px-4 rounded-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                        >
                            <Plus className="w-5 h-5" />
                            Add New Billing Date
                        </button>
                    )}
                </div>
            </div>
      <MasterTable
        columns={
          columns
        }
        data={
          list
        }
        currentPage={
          page
        }
        totalPages={
          totalPages
        }
        totalCount={
          totalCount
        }
        canEdit={
          canUpdate
        }
        onEdit={(
          item
        ) => {
          setEditingItem(
            item
          );

          setIsModalOpen(
            true
          );
        }}
        onPageChange={(
          p
        ) => {
          setPage(
            p
          );

          loadData(
            p
          );
        }}
      />

      <MasterModal
        isOpen={
          isModalOpen
        }
        title={
          editingItem
            ? "Update Billing Date"
            : "Add Billing Date"
        }
        onClose={() => {
          setIsModalOpen(
            false
          );

          setEditingItem(
            null
          );

          formRef.current?.reset();
        }}
        onSubmit={() =>
          formRef.current?.submit()
        }
      >
        <AddTargetDayForm
          ref={
            formRef
          }
          defaultValues={
            editingItem
              ? {
                  From_Day:
                    editingItem.From_Day,

                  To_Day:
                    editingItem.To_Day,

                  Target_Day:
                    editingItem.Target_Day,
                }
              : undefined
          }
          onSubmit={
            handleSubmit
          }
          isLoading={
            isLoading
          }
        />
      </MasterModal>

    </>
  );
}